import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnyChatForm, CrmRichTextEditor } from '@anyx/feature-crm/shared';

import { useBroadcastTrackingLink } from '../hooks';
import { LineMessageFormSubView, useLineMessageStore } from '../store';
import { LineMessageFormFieldsProps, PlainTextBlockFieldErrors } from '../types';

export function PlainTextBlock({ view: { blockIndex } }: { view: LineMessageFormSubView }) {
  const { t } = useTranslation('crm');
  const { formState, control, setValue } = useFormContext<LineMessageFormFieldsProps>();
  const isFormDisabled = useLineMessageStore((store) => store.isFormDisabled);
  const variablePluginOptions = useLineMessageStore((store) => store.variablePluginOptions);

  const { insertTrackingLink } = useBroadcastTrackingLink();

  const trackingEnabled = useWatch({
    control,
    name: 'trackingEnabled',
  });

  const textError = useMemo(() => {
    const errorMsg = formState.errors?.messages?.[blockIndex] as PlainTextBlockFieldErrors;

    if (errorMsg && 'text' in errorMsg) {
      return errorMsg.text?.message;
    }

    return undefined;
  }, [blockIndex, formState]);

  return (
    <AnyChatForm.Block className="overflow-auto">
      <Controller
        control={control}
        name={`messages.${blockIndex}.richText` as const}
        render={({ field: { onChange, value } }) => (
          <CrmRichTextEditor
            wrapper={{
              required: true,
              label: t('crm.lineMessageForm.fields.text.label'),
              error: textError,
            }}
            variablePluginOptions={variablePluginOptions}
            withTrackingLink={trackingEnabled}
            editable={!isFormDisabled}
            initialEditorState={value}
            onChange={({ richText, text }) => {
              onChange(richText);
              setValue(`messages.${blockIndex}.text`, text);
            }}
            insertTrackingLink={insertTrackingLink}
          />
        )}
      />
    </AnyChatForm.Block>
  );
}
